// use client: carousel slide state and the external-link confirm modal
'use client'

import type { PromotionView } from '@/types/cms'

import { useState } from 'react'
import { Col, Container, Modal, Row } from 'react-bootstrap'
import parse from 'html-react-parser'
import moment from 'moment'
import { useRouter } from 'next/navigation'

import { NormalButton, PrimaryButton } from '@/components/buttons'
import { SlideCarousel } from '@/components/carousel/SlideCarousel'

import './../sections.css'

import BackIcon from '@/assets/images/left.svg'
import NextIcon from '@/assets/images/right.svg'
import { cmsImageSrc } from '@/utils/cmsImageSrc'

function Promotions({ initialData = null }: { initialData?: PromotionView[] | null }) {
  const router = useRouter()

  const [currentSlide, setSlide] = useState(0)
  // Content comes from the build-time snapshot only. Every caller now seeds
  // `initialData`, so the old `if (!initialData)` fetch branch was dead. A Strapi
  // publish rebuilds and redeploys the site, so there is nothing to revalidate on
  // mount. See tests/no-client-cms-fetch.spec.ts.
  const isLoading = !initialData
  const promotionsContent: PromotionView[] = initialData ?? []
  const [isAlert, setIsAlert] = useState(false)
  const [externalLink, setExternalLink] = useState<string>()

  /* Arrow logic */
  const isFirstSlide = currentSlide === 0
  const isLastSlide = currentSlide === promotionsContent.length - 1

  const goPrev = () => {
    if (!isFirstSlide) setSlide(prev => prev - 1)
  }

  const goNext = () => {
    if (!isLastSlide) setSlide(prev => prev + 1)
  }

  /* Image slides */
  const slides = promotionsContent.map((item, index) => {
    const cleanTitle =
      typeof item.shortTitle === 'string' ? item.shortTitle.replace(/<[^>]*>/g, '') : null

    // First slide is the LCP element — load it eagerly with high priority
    const isFirst = index === 0
    return (
      <img
        key={item.slug}
        src={cmsImageSrc(item.image || item.imageLink)}
        className="promotion-image"
        loading={isFirst ? 'eager' : 'lazy'}
        fetchPriority={isFirst ? 'high' : undefined}
        width={400}
        height={400}
        alt={
          cleanTitle
            ? `${cleanTitle} (${item.slug})`
            : `alrajhi bank promotion ${index + 1} (${item.slug})`
        }
      />
    )
  })

  /* Text slides */
  const content = promotionsContent.map(item => (
    <div key={item.slug} className="text-start">
      <h2 className="short-title mb-4">
        {typeof item.shortTitle === 'string' ? parse(item.shortTitle) : item.shortTitle}
      </h2>

      <div className="my-2 short-desc">
        {typeof item.shortDescription === 'string'
          ? parse(item.shortDescription)
          : item.shortDescription}
      </div>

      {(item.period || item.startDate) && (
        <div className="mt-2 mb-4 short-desc">
          Campaign period:{' '}
          <span className="fw-bold text-secondary">
            {item.period ||
              `${moment(item.startDate).format('DD MMM YYYY')} - ${moment(item.endDate).format(
                'DD MMM YYYY'
              )}`}
          </span>
        </div>
      )}

      <PrimaryButton
        text="Learn more"
        ariaLabel={`Learn more: ${
          typeof item.shortTitle === 'string'
            ? item.shortTitle.replace(/<[^>]*>/g, '').trim()
            : item.slug
        }`}
        classname="link-button mt-3 mb-3 fs-14"
        onClick={() => {
          if (item.href) return (window.location.href = item.href)
          if (item.isExternalLink) {
            setExternalLink(item.externalLink)
            setIsAlert(true)
            return
          }
          router.push(`/personal/promotions/${item.slug}`)
        }}
      />
    </div>
  ))

  if (isLoading) {
    return (
      <section className="section promotions">
        <div className="container py-5">
          <Container fluid>
            <Row className="g-5">
              <Col md="7" lg="5">
                <div className="promotions-skeleton promotions-skeleton--image" />
              </Col>
              <Col md="5" lg="7" className="align-self-center">
                <div className="promotions-skeleton promotions-skeleton--dots mb-3" />
                <div className="promotions-skeleton promotions-skeleton--title mb-3" />
                <div
                  className="promotions-skeleton promotions-skeleton--title"
                  style={{ width: '60%' }}
                />
                <div className="promotions-skeleton promotions-skeleton--text mt-3" />
                <div
                  className="promotions-skeleton promotions-skeleton--text mt-2"
                  style={{ width: '80%' }}
                />
                <div
                  className="promotions-skeleton promotions-skeleton--text mt-2"
                  style={{ width: '50%' }}
                />
                <div className="promotions-skeleton promotions-skeleton--button mt-4" />
              </Col>
            </Row>
          </Container>
        </div>
      </section>
    )
  }

  return (
    <section className="section promotions">
      {/* External link modal */}
      <Modal
        show={isAlert}
        centered
        onHide={() => setIsAlert(false)}
        aria-label="Leaving alrajhi bank Malaysia website"
      >
        <Modal.Body>
          <h3>
            <b>You are now leaving alrajhi bank Malaysia website</b>
          </h3>
          <p>You are leaving the Bank’s website. External content is not endorsed by the Bank.</p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <NormalButton text="Close" onClick={() => setIsAlert(false)} />
          <PrimaryButton text="Continue" onClick={() => window.open(externalLink, '_blank')} />
        </Modal.Footer>
      </Modal>

      <Container className="py-5">
        <Row className="g-5">
          {/* Image Carousel */}
          <Col md={5}>
            <SlideCarousel index={currentSlide} onIndexChange={setSlide}>
              {slides}
            </SlideCarousel>
          </Col>

          {/* Content + arrows */}
          <Col md={7} className="mt-4 mt-md-5">
            <div className="mt-0 mt-md-4">
              <SlideCarousel index={currentSlide} onIndexChange={setSlide}>
                {content}
              </SlideCarousel>
            </div>

            {/* Actions area */}
            <div className="promo-arrow-wrapper mt-md-2 mt-0">
              <button
                type="button"
                className={`promo-arrow ${
                  isFirstSlide ? 'promo-arrow--disabled' : 'promo-arrow--active'
                }`}
                onClick={goPrev}
                disabled={isFirstSlide}
                aria-label="Previous promotion"
              >
                <img src={BackIcon.src} alt="Previous" />
              </button>

              <button
                type="button"
                className={`promo-arrow ${
                  isLastSlide ? 'promo-arrow--disabled' : 'promo-arrow--active'
                }`}
                onClick={goNext}
                disabled={isLastSlide}
                aria-label="Next promotion"
              >
                <img src={NextIcon.src} alt="Next" />
              </button>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  )
}

export default Promotions
